import type { ProjectRun } from "@/api/projects";
import type { FlowRunFeedSummary } from "@/api/runs";
import { ACTIVE_RUN_STATES, ANALYZABLE_RUN_STATES } from "@/api/runs";
import type { FlowRunGroup } from "@/components/runs/grouped/group-runs";
import { groupRunFeed } from "@/components/runs/grouped/group-runs";

/**
 * What the Analysis surface lists (fs#1861): one entry per thing a user
 * SUBMITTED — a flow run, or a standalone run — never one per flow step.
 *
 * Before this, one AFFDO submission showed up as a row per node run
 * (`merge_fragments`, `torsion_fit`, `gaff_evaluation`, …), each labelled with
 * the flow's name, and the user had to guess which step held the analysis.
 * The grouping is the Runs tab's own ({@link groupRunFeed}, fs#1799), not a
 * second rule, so the two tabs cannot disagree about what one flow run is.
 */

/** A settled flow run with at least one step an analysis can read. */
export interface AnalysisFlowRunEntry {
  kind: "flow";
  flowRunId: string;
  flowName: string | null;
  /** Every step the feed returned, oldest first. */
  steps: ProjectRun[];
  /** The step the analysis is read through — see {@link analysisStepOf}. */
  step: ProjectRun;
  /** Every step of the flow run, from its rollup — so steps outside the
   * feed's window count too. With no rollup, only the window's steps
   * (`countsFrom` says which). */
  stepCount: number;
  /** Steps that ended `failed_*`, counted the same way. */
  failedSteps: number;
  /** `window` when the counts cover only the steps the feed returned
   * (fs#1870): a flow run with hundreds of steps must not read as twelve. */
  countsFrom: "flow-run" | "window";
  /** `settled` when the flow run's rollup says so; `unknown` when there is no
   * rollup to ask ({@link flowRunSettlement}). Only a `settled` flow run has
   * its report generated on open. */
  settlement: "settled" | "unknown";
  /** When the flow run finished — from its rollup, or, with no rollup, the
   * newest `finished_at` among the steps the feed returned (`finishedAtFrom`
   * says which). `null` when none was recorded. */
  finishedAt: string | null;
  finishedAtFrom: "flow-run" | "latest-step";
}

export interface AnalysisRunEntry {
  kind: "run";
  run: ProjectRun;
}

export type AnalysisEntry = AnalysisFlowRunEntry | AnalysisRunEntry;

export interface AnalysisEntries {
  /** Newest first, in the Runs tab's order. */
  entries: AnalysisEntry[];
  /** Flow runs with a step still in flight, plus in-flight standalone runs. */
  activeCount: number;
  /** Settled flow runs and standalone runs with nothing an analysis can read. */
  unanalyzableCount: number;
}

/**
 * The rows the Analysis pages group: this project's rows from the tenant feed
 * (`GET /api/runs`), plus any project-feed row the tenant feed did not return.
 *
 * Where both feeds hold a run, the tenant feed's row wins (fs#1861, reopened).
 * It is the response the flow-run rollups come from, so its rows and its
 * rollups describe one snapshot. Grouping the project feed's rows against
 * those rollups instead listed every step of a flow run on dev: those rows
 * came back without `flow_run_id` — a body the browser had cached before the
 * key existed, revalidated by an ETag that did not cover the response's shape
 * (fixed server-side: every ETag now names the build that rendered it) — so
 * each step fell through as a standalone run. The project feed still fills in
 * when the tenant feed failed, or for a run older than its window.
 */
export function analysisRows(
  feedRuns: readonly ProjectRun[],
  projectRuns: readonly ProjectRun[],
  slug: string
): ProjectRun[] {
  const rows = feedRuns.filter((r) => r.project_slug === slug);
  const seen = new Set(rows.map((r) => r.run_id));
  return [...rows, ...projectRuns.filter((r) => !seen.has(r.run_id))];
}

/**
 * The step of a flow run whose analysis page the flow run opens on: the
 * NEWEST step that can carry an analysis (`ANALYZABLE_RUN_STATES`), or `null`
 * when none can.
 *
 * Which step does not change the report's content: the assembler reads the
 * whole submission's artifacts whichever step it is keyed to (the cohort,
 * fs#1228). It matters because a report is looked up by the step it was
 * generated for, so the list and the page must pick the same one — which is
 * why this is one function both call. On a SETTLED flow run
 * ({@link isSettled}) the newest step is its last node (`merge_fragments`
 * today), which the recent-rows window always holds; when that step failed
 * for an infrastructure reason the newest step that did finish takes its
 * place, rather than the flow run dropping out of the list.
 *
 * `steps` must be oldest first, as {@link groupRunFeed} returns them and as
 * `GET /api/flow-runs/{id}` lists them — the flow-run page calls this too, so
 * it opens the same step's report.
 */
export function analysisStepOf<T extends Pick<ProjectRun, "status">>(
  steps: readonly T[]
): T | null {
  for (let i = steps.length - 1; i >= 0; i -= 1) {
    const step = steps[i] as T;
    if (ANALYZABLE_RUN_STATES.has(step.status)) return step;
  }
  return null;
}

/** One flow run among `runs`, with its rollup from `flowRuns` (or one derived
 * from its steps when the server sent none) — `null` when `runs` holds none of
 * its steps. Steps oldest first. */
export function flowRunGroup(
  runs: ProjectRun[],
  flowRuns: readonly FlowRunFeedSummary[],
  flowRunId: string
): FlowRunGroup | null {
  const group = groupRunFeed(
    runs.filter((r) => r.flow_run_id === flowRunId),
    flowRuns.filter((s) => s.flow_run_id === flowRunId)
  ).find((e) => e.kind === "flow");
  return group?.kind === "flow" ? group : null;
}

/**
 * True once the flow run has settled — judged by its ROLLUP, never by the
 * steps a feed window happened to return (Rupert PR-1865 r1).
 *
 * AFFDO flows fan out per fragment and per torsion, so a step still in flight
 * can sit outside the recent-rows window while every step inside it has
 * finished. The server's rollup (`flow_runs[]` on `GET /api/runs`, fs#1799)
 * does not have that blind spot: its `status` is the flow run's own latched
 * `flow_runs.status` (`running` until every node run has settled), and its
 * step counts cover every child, in the window or not. With no `status` (a
 * child written before #371 has no `flow_runs` row), every counted step must
 * have settled. A summary {@link groupRunFeed} DERIVED for want of a server
 * rollup is not a rollup at all — see {@link flowRunSettlement}, which never
 * passes one here.
 */
export function isSettled(summary: FlowRunFeedSummary): boolean {
  if (summary.status !== null) return summary.status !== "running";
  return summary.steps_settled === summary.steps_total;
}

/**
 * Whether a flow run has settled, as far as anyone can tell (Rupert PR-1865 r2).
 *
 * With a server rollup, {@link isSettled} decides. With none — an older server,
 * or `GET /api/runs` failed — the steps in the feed's window can prove a flow
 * run is still RUNNING (one of them is), but never that it has settled: a
 * fanned-out step outside the window may still be in flight. So that case is
 * `unknown`, not `settled`, and nothing is generated for it automatically.
 */
export function flowRunSettlement(group: FlowRunGroup): "settled" | "running" | "unknown" {
  if (!group.derived) return isSettled(group.summary) ? "settled" : "running";
  return group.steps.some((s) => ACTIVE_RUN_STATES.has(s.status)) ? "running" : "unknown";
}

/**
 * The project's runs as the Analysis list shows them.
 *
 * A flow run is listed once it has SETTLED ({@link flowRunSettlement}) — an
 * analysis generated while its fits are still landing would be a snapshot of
 * half a submission — or, with no rollup to say so, once nothing in the window
 * is running (listed as `unknown`); and only if one of its steps can carry an
 * analysis. `flowRuns` is the server's rollups (`GET /api/runs`), which the
 * step and failure counts and the finish time come from too, so they include
 * steps outside the window.
 */
export function analysisEntries(
  runs: ProjectRun[],
  flowRuns: readonly FlowRunFeedSummary[]
): AnalysisEntries {
  const entries: AnalysisEntry[] = [];
  let activeCount = 0;
  let unanalyzableCount = 0;

  for (const entry of groupRunFeed(runs, [...flowRuns])) {
    if (entry.kind === "run") {
      if (ANALYZABLE_RUN_STATES.has(entry.run.status)) entries.push(entry);
      else if (ACTIVE_RUN_STATES.has(entry.run.status)) activeCount += 1;
      else unanalyzableCount += 1;
      continue;
    }
    const settlement = flowRunSettlement(entry);
    if (settlement === "running") {
      activeCount += 1;
      continue;
    }
    const step = analysisStepOf(entry.steps);
    if (step === null) {
      unanalyzableCount += 1;
      continue;
    }
    entries.push({
      kind: "flow",
      flowRunId: entry.flowRunId,
      flowName: entry.summary.flow_name,
      steps: entry.steps,
      step,
      stepCount: entry.summary.steps_total,
      failedSteps: entry.summary.steps_failed,
      countsFrom: entry.derived ? "window" : "flow-run",
      settlement,
      // The rollup's own finish time, never the window's (Rupert PR-1865 r2):
      // the counts beside it cover every step, so the time must too.
      finishedAt: entry.derived ? newestFinishedAt(entry.steps) : entry.summary.finished_at,
      finishedAtFrom: entry.derived ? "latest-step" : "flow-run",
    });
  }
  return { entries, activeCount, unanalyzableCount };
}

function newestFinishedAt(steps: readonly ProjectRun[]): string | null {
  let newest: string | null = null;
  for (const s of steps) {
    if (s.finished_at && (newest === null || Date.parse(s.finished_at) > Date.parse(newest))) {
      newest = s.finished_at;
    }
  }
  return newest;
}
